import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons'
import { useLabraChatSelector } from '../../../modules/labraChat/context'
import { NextBestAction } from '../../../modules/labraChat/reducer'
import {
  selectLabraChatNextBestActions,
  selectIsLabraChatLoading,
  selectIsLabraChatGeneratingNextActions,
} from '../../../modules/labraChat/selectors'
import { useStyles } from './SuggestionChips.styles'

/** Starter prompts shown before the agent has emitted any `next_best_actions`. */
const DEFAULT_SUGGESTIONS = [
  'What has been the funding revenue this Quarter?',
  'See my wallet summary',
  'Show my recent activities',
  'What opportunities are at risk?',
]

/** Horizontal scroll step when an arrow is clicked. */
const SCROLL_STEP = 200

interface SuggestionChipsProps {
  onSelect: (prompt: string) => void
  /**
   * Force the single-row horizontal slider. Set once a conversation has
   * started — on the landing screen (no messages yet) the chips wrap across
   * multiple rows instead.
   */
  singleLine?: boolean
}

export const SuggestionChips: React.FC<SuggestionChipsProps> = ({
  onSelect,
  singleLine = false,
}) => {
  const { classes, cx } = useStyles()
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const nextBestActions = useLabraChatSelector(selectLabraChatNextBestActions)
  const isLoading = useLabraChatSelector(selectIsLabraChatLoading)
  const isGeneratingNextActions = useLabraChatSelector(
    selectIsLabraChatGeneratingNextActions
  )

  // Dynamic agent CTAs (label + prompt) take precedence; otherwise fall back to
  // the static default chips where the chip text doubles as the prompt.
  const items: NextBestAction[] = useMemo(
    () =>
      nextBestActions.length > 0
        ? nextBestActions
        : DEFAULT_SUGGESTIONS.map(s => ({ label: s, prompt: s })),
    [nextBestActions]
  )

  // Landing screen wraps the chips across rows; once a conversation has started
  // they collapse into a single-row horizontal slider.
  const isSlider = singleLine

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
  }, [])

  useLayoutEffect(() => {
    updateScrollState()
    const scroller = scrollRef.current
    const observer = new ResizeObserver(updateScrollState)
    if (scroller) observer.observe(scroller)
    return () => observer.disconnect()
  }, [updateScrollState, items, isSlider])

  // While the agent computes its follow-up actions, show a "generating" state
  // in the chip slot — it gets replaced by the chips the moment they arrive.
  if (isGeneratingNextActions && nextBestActions.length === 0) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.generatingRow}>
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            beatFade
            className={classes.generatingIcon}
          />
          <span className={classes.generatingText}>
            Generating next best actions for you
          </span>
        </div>
      </div>
    )
  }

  // Hide the chips while a response is in flight and no fresh CTAs have arrived
  // yet. The prior turn's next_best_actions are cleared on send, so anything
  // shown now would just be the stale default fallback. They reappear the
  // moment the new next_best_actions land (or loading ends for the idle state).
  if (isLoading && nextBestActions.length === 0) return null

  const scrollBy = (delta: number) =>
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' })

  const handleChipClick = (prompt: string) => {
    setSelectedPrompt(prompt)
    onSelect(prompt)
  }

  // Key by index, not label: agent CTAs can repeat a label, which would collide
  // as a React key (and the chip is rendered in both the measure clone and the
  // visible list).
  const renderChip = (item: NextBestAction, index: number) => (
    <button
      key={index}
      className={cx(classes.chip, {
        selected: selectedPrompt === item.prompt,
      })}
      onClick={() => handleChipClick(item.prompt)}
      type="button"
    >
      {item.label}
    </button>
  )

  return (
    <div className={classes.wrapper}>
      {isSlider && canScrollLeft && (
        <button
          className={cx(classes.scrollArrow, classes.scrollArrowLeft)}
          onClick={() => scrollBy(-SCROLL_STEP)}
          type="button"
          aria-label="Scroll suggestions left"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      <div
        className={cx(classes.root, isSlider && classes.rootSlider)}
        ref={scrollRef}
        onScroll={updateScrollState}
      >
        {items.map(renderChip)}
      </div>

      {isSlider && canScrollRight && (
        <button
          className={cx(classes.scrollArrow, classes.scrollArrowRight)}
          onClick={() => scrollBy(SCROLL_STEP)}
          type="button"
          aria-label="Scroll suggestions right"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  )
}
